import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Servicio base para comunicación con la API administrativa.
 *
 * @remarks
 * Proporciona métodos genéricos tipados para operaciones HTTP.
 * El interceptor inyecta automáticamente el JWT.
 *
 * @example
 * ```typescript
 * this.adminApi.get<PersonaAdminDTO>('/personas/1').subscribe(...);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AdminApiService {
  private readonly apiUrl: string;

  constructor(private readonly http: HttpClient) {
    this.apiUrl = `${environment.url_ms_base}${environment.login_conf.DOMAIN_ROUTE}${environment.admin_conf.DOMAIN_ROUTE}`;
  }

  /**
   * Realiza una petición GET.
   *
   * @template T - Tipo de respuesta esperada
   * @param path - Ruta relativa al endpoint de admin
   * @returns Observable con la respuesta tipada
   */
  get<T>(path: string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}${path}`);
  }

  /**
   * Realiza una petición POST.
   *
   * @template T - Tipo de respuesta esperada
   * @param path - Ruta relativa al endpoint de admin
   * @param body - Cuerpo de la petición (tipado)
   * @returns Observable con la respuesta tipada
   */
  post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${path}`, body);
  }

  /**
   * Realiza una petición PUT.
   *
   * @template T - Tipo de respuesta esperada
   * @param path - Ruta relativa al endpoint de admin
   * @param body - Cuerpo de la petición (tipado)
   * @returns Observable con la respuesta tipada
   */
  put<T>(path: string, body: unknown): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}${path}`, body);
  }

  /**
   * Realiza una petición PATCH.
   *
   * @template T - Tipo de respuesta esperada
   * @param path - Ruta relativa al endpoint de admin
   * @param body - Cuerpo de la petición (tipado)
   * @returns Observable con la respuesta tipada
   */
  patch<T>(path: string, body: unknown): Observable<T> {
    return this.http.patch<T>(`${this.apiUrl}${path}`, body);
  }

  /**
   * Realiza una petición DELETE.
   *
   * @param path - Ruta relativa al endpoint de admin
   * @returns Observable que completa al eliminar
   */
  delete(path: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${path}`);
  }
}
